import React, {
  useContext, useEffect, useState, useCallback, useRef,
} from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import PropTypes from 'prop-types';
import FlextimeContext from './FlextimeContext';
import { onCreateModel, onUpdateModel } from '../../Helpers/GraphQL/Subscription';
import SidebarContext from '../Sidebar/SidebarContext';
import { deleteRecord } from '../../Helpers/GraphQL/Mutation';
import TimeWindowContext from '../TimeWindow/TimeWindowContext';
import { listModelsNew } from '../../Helpers/GraphQL/Query';

const FlextimeProvider = ({ children }) => {
  const { selected } = useContext(SidebarContext);
  const { start, end } = useContext(TimeWindowContext);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [previousLoad, setPreviousLoad] = useState({});
  const subscriptionsRef = useRef([]);
  const [isWindowActive, setIsWindowActive] = useState(true);
  const [lastFocusTime, setLastFocusTime] = useState(Date.now());

  const action = async (operation, variables) => {
    setLoading(true);
    await API.graphql(graphqlOperation(operation, variables));
    setLoading(false);
  };

  const deleteModel = (id) => {
    const key = 'deleteFlextime';

    deleteRecord(key, id).then(() => {
      setDataSource((prevDataSource) => prevDataSource.filter((record) => record.id !== id));
    });
  };

  const setupSubscriptions = useCallback((params) => {
    // Unsubscribe from existing subscriptions
    subscriptionsRef.current.forEach((sub) => sub.unsubscribe());

    const handleSubscriptionError = (err) => {
      // eslint-disable-next-line no-console
      console.warn('Flextime subscription error:', err);
      // Only attempt to reconnect after a longer delay and if window is still active
      // This prevents rapid reconnection attempts that could cause flickering
      setTimeout(() => {
        if (isWindowActive && document.visibilityState === 'visible') {
          setupSubscriptions(params);
        }
      }, 10000); // Increased delay to 10 seconds
    };

    // Create new subscriptions with error handling
    const newSubscriptions = [
      onCreateModel('onCreateFlextime', params, setDataSource, setLoading, handleSubscriptionError),
      onUpdateModel('onUpdateFlextime', params, setDataSource, setLoading, handleSubscriptionError),
    ];

    subscriptionsRef.current = newSubscriptions;
  }, [isWindowActive]);

  const refreshData = useCallback(() => {
    if (selected && start && end) {
      listModelsNew('listFlexTimesByUpdatedAt', selected, start, end, setDataSource, setLoading);
    }
  }, [selected, start, end]);

  // Handle window focus/blur for reconnection
  useEffect(() => {
    let focusTimeout;

    const handleFocus = () => {
      const now = Date.now();
      const timeSinceLastFocus = now - lastFocusTime;

      setIsWindowActive(true);
      setLastFocusTime(now);

      // Only refresh if it's been more than 30 seconds since last focus
      // This prevents rapid re-renders when quickly switching tabs
      if (selected && timeSinceLastFocus > 30000) {
        // Clear any pending refresh to avoid multiple calls
        if (focusTimeout) clearTimeout(focusTimeout);

        // Debounce the refresh to prevent multiple rapid calls
        focusTimeout = setTimeout(() => {
          refreshData();
          setupSubscriptions({ owner: selected });
        }, 500);
      }
    };

    const handleBlur = () => {
      setIsWindowActive(false);
      // Clear any pending refresh when losing focus
      if (focusTimeout) clearTimeout(focusTimeout);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      } else {
        handleBlur();
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (focusTimeout) clearTimeout(focusTimeout);
    };
  }, [selected, refreshData, setupSubscriptions, lastFocusTime]);

  useEffect(() => {
    const { selected: prevSelected, start: prevStart } = previousLoad;

    if (selected !== prevSelected) {
      const params = { owner: selected };
      setupSubscriptions(params);
      setPreviousLoad((prevPreviousLoad) => ({ ...prevPreviousLoad, selected }));
    }

    if (selected !== prevSelected || start !== prevStart) {
      refreshData();
      setPreviousLoad((prevPreviousLoad) => ({ ...prevPreviousLoad, start }));
    }
  }, [selected, start, end, previousLoad, refreshData, setupSubscriptions]);

  // Cleanup subscriptions on unmount
  useEffect(() => () => {
    subscriptionsRef.current.forEach((sub) => sub.unsubscribe());
    subscriptionsRef.current = [];
  }, []);

  return (
    <FlextimeContext.Provider value={{
      dataSource, loading, deleteModel, action,
    }}
    >
      {children}
    </FlextimeContext.Provider>
  );
};

FlextimeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FlextimeProvider;
